/**
 * /admin/explore — Explore Influencers
 * Replaces the old Match History page with a rich spreadsheet + filter UI,
 * inline expanded reports, PDF download, and a Google Sheet mass-import wizard.
 */

import { useState, useEffect, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronDown, ChevronUp, Sparkles, Package, Loader2,
  Search, X, FileDown, Upload, Users, AlertCircle,
  CheckCircle2, ChevronRight, Filter,
} from 'lucide-react';
import { PageHeader, StatusBadge, EmptyState, DataTableSkeleton } from '../components/ui';
import { API_BASE, proxyImageUrl } from '../api';
import { useDemo } from '../context/DemoContext';
import { exportMatchReport } from '../lib/exportReport';
import { DEMO_EXPLORE_RUNS, DEMO_RUN_RESULTS } from '../demo/data';

// ── Types ─────────────────────────────────────────────────────────────────

interface AudienceMetricRow { label: string; followers_pct?: number }

interface RunSummary {
  id: number;
  created_at: string;
  gmv_low: number;
  gmv_expected: number;
  gmv_high: number;
  tier: number;
  confidence: number;
  assumptions?: {
    platform_details?: Array<{ platform: string; handle: string }>;
    is_multi_platform?: boolean;
    aov_estimated?: number;
    summary_text?: string;
    [key: string]: unknown;
  } | null;
  creator_analyses: {
    followers: number | null;
    engagement_rate: number | null;
    primary_category: string | null;
    provider: string;
    profile_image_url: string | null;
    interests_ranked: AudienceMetricRow[] | null;
    brand_affinity_ranked: AudienceMetricRow[] | null;
    audience_gender_affinity: string | null;
    derived_features: unknown;
    geo_top_countries: Array<{ country: string; pct: number }> | null;
    avg_views: number | null;
    audience_age_min: number | null;
    audience_age_max: number | null;
    creators: { platform: string; handle: string; profile_url: string };
  };
}

interface FactorEntry { weight: number; raw_score: number; contribution: number; why: string }
interface FactorBreakdown {
  category: FactorEntry; tags: FactorEntry; age: FactorEntry; price: FactorEntry;
  margin: FactorEntry; complexity: FactorEntry; proof: FactorEntry;
  interest_affinity: FactorEntry; gender_penalty?: FactorEntry; total_score: number;
}
interface RunResultItem {
  rank: number;
  score: number;
  factor_breakdown: FactorBreakdown;
  products: {
    id: number; title: string; primary_image_url: string | null;
    price_min: number; price_max: number; vertical: string; category: string; tags: string[];
  } | null;
}

// ── Helpers ───────────────────────────────────────────────────────────────

function fmtMoney(n: number) {
  return '$' + n.toLocaleString('en-US', { maximumFractionDigits: 0 });
}
function fmtNum(n: number | null) {
  if (n == null) return '—';
  return n >= 1_000_000
    ? `${(n / 1_000_000).toFixed(1)}M`
    : n >= 1_000 ? `${(n / 1_000).toFixed(0)}K` : String(n);
}
function fmtDate(s: string) {
  return new Date(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

const PLATFORM_COLORS: Record<string, string> = {
  instagram: '#E4405F', tiktok: '#00f2ea', youtube: '#FF0000',
};
const PLATFORM_LABEL: Record<string, string> = {
  instagram: 'IG', tiktok: 'TT', youtube: 'YT',
};

function PlatformBadge({ platform }: { platform: string }) {
  const color = PLATFORM_COLORS[platform] ?? '#888';
  const label = PLATFORM_LABEL[platform] ?? platform.slice(0, 2).toUpperCase();
  return (
    <span
      className="inline-flex items-center px-1.5 py-0.5 text-[9px] font-bold uppercase"
      style={{ backgroundColor: `${color}18`, color, border: `1px solid ${color}30`, borderRadius: '2px' }}
    >
      {label}
    </span>
  );
}

function TierBadge({ tier }: { tier: number }) {
  const map: Record<number, { label: string; variant: 'success' | 'info' | 'neutral' }> = {
    1: { label: 'Tier 1', variant: 'success' },
    2: { label: 'Tier 2', variant: 'info' },
    3: { label: 'Tier 3', variant: 'neutral' },
  };
  const t = map[tier] ?? map[3];
  return <StatusBadge variant={t.variant}>{t.label}</StatusBadge>;
}

function ScoreChip({ score }: { score: number }) {
  const pct = Math.round(score * 100);
  const color = pct >= 70
    ? 'text-gold bg-gold/10 border-gold/20'
    : pct >= 50
      ? 'text-amber-300 bg-amber-400/10 border-amber-400/20'
      : 'text-white/50 bg-white/[0.04] border-white/[0.10]';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-xs font-bold border ${color}`} style={{ borderRadius: '2px' }}>
      {pct}%
    </span>
  );
}

function CreatorAvatar({ url, handle, size = 36 }: { url: string | null; handle: string; size?: number }) {
  const [err, setErr] = useState(false);
  const initials = handle.replace('@', '').slice(0, 2).toUpperCase();
  if (!url || err) {
    return (
      <div
        className="flex items-center justify-center text-[11px] font-bold text-white/50 shrink-0 select-none"
        style={{ width: size, height: size, borderRadius: '50%', backgroundColor: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.1)' }}
      >
        {initials}
      </div>
    );
  }
  return (
    <img
      src={proxyImageUrl(url) ?? undefined}
      referrerPolicy="no-referrer"
      className="object-cover shrink-0"
      style={{ width: size, height: size, borderRadius: '50%', border: '1px solid rgba(255,255,255,0.1)' }}
      alt={handle}
      onError={() => setErr(true)}
    />
  );
}

// ── Social URL parser ─────────────────────────────────────────────────────
// Strips query-string / hash / trailing slashes so only the clean profile
// URL is stored.  Handles tracking params like ?igsh=…, ?_t=…, ?hl=… etc.

function parseSocialUrl(raw: string): { platform: string; handle: string; profile_url: string } | null {
  const url = raw.trim();

  // Normalise: add protocol if missing so URL() can parse it
  let normalized = url;
  if (!normalized.startsWith('http://') && !normalized.startsWith('https://')) {
    normalized = 'https://' + normalized;
  }

  // Try to extract handle + clean URL using the URL class (handles all query/hash variants)
  try {
    const parsed = new URL(normalized);
    const host   = parsed.hostname.toLowerCase().replace(/^www\./, '');
    const parts  = parsed.pathname.replace(/\/$/, '').replace(/^\//, '').split('/').filter(Boolean);

    const cleanUrl = (h: string) => `${parsed.origin}/${h}`;

    if (host.includes('tiktok.com') && parts[0]) {
      const handle = parts[0].replace(/^@/, '');
      return { platform: 'tiktok', handle: `@${handle}`, profile_url: cleanUrl(`@${handle}`) };
    }
    if (host.includes('instagram.com') && parts[0] && parts[0] !== 'p' && parts[0] !== 'stories' && parts[0] !== 'reel') {
      const handle = parts[0].replace(/^@/, '');
      return { platform: 'instagram', handle: `@${handle}`, profile_url: cleanUrl(handle) };
    }
    if (host.includes('youtube.com')) {
      if (parts[0] === 'channel' && parts[1]) {
        return { platform: 'youtube', handle: parts[1], profile_url: cleanUrl(`channel/${parts[1]}`) };
      }
      if (parts[0] === 'c' && parts[1]) {
        return { platform: 'youtube', handle: `@${parts[1]}`, profile_url: cleanUrl(`c/${parts[1]}`) };
      }
      if (parts[0]?.startsWith('@')) {
        const handle = parts[0].replace(/^@/, '');
        return { platform: 'youtube', handle: `@${handle}`, profile_url: cleanUrl(`@${handle}`) };
      }
    }
  } catch { /* fall through to regex fallback */ }

  // Regex fallback for URLs the URL constructor couldn't parse
  const tiktok    = url.match(/tiktok\.com\/@?([\w.]+)/);
  const instagram = url.match(/instagram\.com\/([A-Za-z0-9_.]+)(?:[/?#]|$)/);
  const youtube   = url.match(/youtube\.com\/@([\w.-]+)/);
  const yt2       = url.match(/youtube\.com\/c\/([\w.-]+)/);
  const yt3       = url.match(/youtube\.com\/channel\/([\w.-]+)/);
  if (tiktok)    return { platform: 'tiktok',    handle: `@${tiktok[1]}`,    profile_url: `https://www.tiktok.com/@${tiktok[1]}` };
  if (instagram) return { platform: 'instagram', handle: `@${instagram[1]}`, profile_url: `https://www.instagram.com/${instagram[1]}` };
  if (youtube)   return { platform: 'youtube',   handle: `@${youtube[1]}`,   profile_url: `https://www.youtube.com/@${youtube[1]}` };
  if (yt2)       return { platform: 'youtube',   handle: `@${yt2[1]}`,       profile_url: `https://www.youtube.com/c/${yt2[1]}` };
  if (yt3)       return { platform: 'youtube',   handle: yt3[1],             profile_url: `https://www.youtube.com/channel/${yt3[1]}` };
  return null;
}

function parseSheetCsv(csv: string): { creators: ParsedCreator[]; undetected: string[] } {
  const lines = csv.split('\n').map(l => l.trim()).filter(Boolean);
  const creators: ParsedCreator[] = [];
  const undetected: string[] = [];
  const startIdx = lines[0]?.toLowerCase().includes('name') && lines[0]?.toLowerCase().includes('url') ? 1 : 0;
  for (const line of lines.slice(startIdx)) {
    const cols = line.split(/\t|,(?=(?:[^"]*"[^"]*")*[^"]*$)/).map(c => c.replace(/^"|"$/g, '').trim());
    let found: ParsedCreator | null = null;
    let name = '';
    for (const col of cols) {
      if (!col) continue;
      let parsed = parseSocialUrl(col);
      if (!parsed && col.startsWith('http')) parsed = parseSocialUrl(col);
      if (!parsed) {
        const withHttps = `https://${col}`;
        parsed = parseSocialUrl(withHttps);
      }
      if (parsed) { found = { ...parsed, name }; break; }
      if (!name) name = col;
    }
    if (found) {
      if (!found.name && name) found.name = name;
      creators.push(found);
    } else if (cols.some(c => c.length > 0)) {
      undetected.push(line);
    }
  }
  return { creators, undetected };
}

// ── API helper ────────────────────────────────────────────────────────────

async function apiFetch<T>(path: string, opts: RequestInit = {}): Promise<T> {
  const res = await fetch(`${API_BASE}/admin/creator-match${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...((opts.headers ?? {}) as Record<string, string>) },
    ...opts,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText })) as { error?: string };
    throw new Error(err.error ?? `HTTP ${res.status}`);
  }
  return res.json() as Promise<T>;
}

// ── Build AnalyzeResult for PDF export ────────────────────────────────────

function buildAnalyzeResult(run: RunSummary, results: RunResultItem[]) {
  const ca = run.creator_analyses;
  const cr = ca.creators;
  const caAny = ca as Record<string, unknown>;
  const notes = (caAny.notes ?? {}) as Record<string, unknown>;
  return {
    creator: {
      platform: cr.platform,
      handle: cr.handle,
      profile_url: cr.profile_url,
      profile_image_url: ca.profile_image_url ?? null,
    },
    analysis: {
      followers: ca.followers,
      engagement_rate: ca.engagement_rate,
      avg_views: ca.avg_views ?? null,
      avg_likes: (caAny.avg_likes as number | null) ?? null,
      avg_comments: (caAny.avg_comments as number | null) ?? null,
      following_count: (caAny.following_count as number | null) ?? null,
      posts_count: (caAny.posts_count as number | null) ?? null,
      audience_age_min: ca.audience_age_min ?? null,
      audience_age_max: ca.audience_age_max ?? null,
      audience_gender_affinity: ca.audience_gender_affinity ?? 'unknown',
      primary_category: ca.primary_category,
      secondary_categories: (caAny.secondary_categories as string[] | null) ?? null,
      geo_top_countries: ca.geo_top_countries ?? [],
      credibility_score: (caAny.credibility_score as number | null) ?? null,
      paid_post_performance: (caAny.paid_post_performance as number | null) ?? null,
      gender_distribution: (caAny.gender_distribution as { male?: number; female?: number } | null) ?? null,
      age_distribution: (caAny.age_distribution as Array<{ label: string; followers_pct: number | null }> | null) ?? null,
      geo_distribution: (caAny.geo_distribution as Array<{ label: string; followers_pct: number | null }> | null) ?? null,
      audience_languages: (caAny.audience_languages as Array<{ code: string; name?: string; weight: number }> | null) ?? null,
      audience_cities: (caAny.audience_cities as Array<{ code?: string; name?: string; weight: number }> | null) ?? null,
      audience_ethnicities: (caAny.audience_ethnicities as Array<{ code: string; name?: string; weight: number }> | null) ?? null,
      interests_ranked: (caAny.interests_ranked as Array<{ label: string; followers_pct: number | null }> | null) ?? null,
      brand_affinity_ranked: (caAny.brand_affinity_ranked as Array<{ label: string; followers_pct: number | null }> | null) ?? null,
      derived_features: (caAny.derived_features as Record<string, unknown> | null) ?? null,
      collaboration_brands: (notes.collaboration_brands as Array<Record<string, unknown>> | null) ?? null,
      notes: notes,
      provider: ca.provider,
    },
    match_run_id: run.id,
    top_products: results.map(r => ({
      rank: r.rank,
      title: r.products?.title ?? 'Unknown',
      price_min: r.products?.price_min ?? 0,
      price_max: r.products?.price_max ?? 0,
      vertical: r.products?.vertical ?? '',
      category: r.products?.category ?? '',
      tags: r.products?.tags ?? [],
      score: r.score,
      factor_breakdown: r.factor_breakdown,
      primary_image_url: r.products?.primary_image_url ?? null,
    })),
    gmv: {
      gmv_low: run.gmv_low,
      gmv_expected: run.gmv_expected,
      gmv_high: run.gmv_high,
      tier: run.tier as 1 | 2 | 3,
      confidence: run.confidence,
      aov_estimated: (run.assumptions?.aov_estimated as number | undefined) ?? 45,
      assumptions: run.assumptions ?? {},
    },
  };
}

// ── MassImportModal ───────────────────────────────────────────────────────

interface ParsedCreator {
  name: string;
  platform: string;
  handle: string;
  profile_url: string;
}

interface DuplicateInfo {
  handle: string;
  lastMatchDate: string;
  skip: boolean;
}

type ImportStep = 'input' | 'review' | 'duplicates' | 'progress' | 'done';

function MassImportModal({
  onClose,
  existingRuns,
  onImportComplete,
}: {
  onClose: () => void;
  existingRuns: RunSummary[];
  onImportComplete: () => void;
}) {
  const [step, setStep]             = useState<ImportStep>('input');
  const [sheetInput, setSheetInput] = useState('');
  const [parsed, setParsed]         = useState<ParsedCreator[]>([]);
  const [removed, setRemoved]       = useState<Set<number>>(new Set());
  const [undetected, setUndetected] = useState<string[]>([]);
  const [duplicates, setDuplicates] = useState<DuplicateInfo[]>([]);
  const [progress, setProgress]     = useState({ total: 0, done: 0, success: 0, current: '' });
  const [fetching, setFetching]     = useState(false);
  const [fetchErr, setFetchErr]     = useState<string | null>(null);

  const existingHandleMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const r of existingRuns) {
      const h = (r.creator_analyses?.creators?.handle ?? '').toLowerCase().replace('@', '');
      if (h && !map.has(h)) map.set(h, r.created_at);
    }
    return map;
  }, [existingRuns]);

  async function handleParse() {
    const input = sheetInput.trim();
    if (!input) return;
    setFetching(true);
    setFetchErr(null);
    let csv = input;
    if (input.startsWith('https://docs.google.com/spreadsheets/')) {
      let fetchUrl = input;
      if (!fetchUrl.includes('/export')) {
        const base = fetchUrl.split('/edit')[0].split('/pub')[0];
        const gid  = fetchUrl.match(/gid=(\d+)/)?.[1] ?? '0';
        fetchUrl = `${base}/export?format=csv&gid=${gid}`;
      }
      try {
        const res = await fetch(
          `${API_BASE}/admin/creator-match/fetch-sheet?url=${encodeURIComponent(fetchUrl)}`,
          { credentials: 'include' },
        );
        if (!res.ok) throw new Error('Failed to fetch sheet — make sure it is published to web as CSV');
        csv = await res.text();
      } catch (e) {
        setFetchErr(e instanceof Error ? e.message : 'Sheet fetch failed');
        setFetching(false);
        return;
      }
    }
    const { creators, undetected: und } = parseSheetCsv(csv);
    setUndetected(und);
    setParsed(creators);
    setStep('review');
    setFetching(false);
  }

  function handleReviewNext() {
    const active = parsed.filter((_, i) => !removed.has(i));
    const dupes: DuplicateInfo[] = [];
    for (const c of active) {
      const key = c.handle.replace('@', '').toLowerCase();
      if (existingHandleMap.has(key)) {
        dupes.push({ handle: c.handle, lastMatchDate: existingHandleMap.get(key)!, skip: true });
      }
    }
    if (dupes.length > 0) {
      setDuplicates(dupes);
      setStep('duplicates');
    } else {
      startBatch(active, []);
    }
  }

  async function startBatch(creators: ParsedCreator[], dupes: DuplicateInfo[]) {
    const skipSet = new Set(dupes.filter(d => d.skip).map(d => d.handle.replace('@', '').toLowerCase()));
    const toRun   = creators.filter(c => !skipSet.has(c.handle.replace('@', '').toLowerCase()));
    setProgress({ total: toRun.length, done: 0, success: 0, current: '' });
    setStep('progress');
    let success = 0;
    for (let i = 0; i < toRun.length; i++) {
      const c = toRun[i];
      setProgress(prev => ({ ...prev, current: c.handle, done: i }));
      try {
        await apiFetch('/analyze', {
          method: 'POST',
          body: JSON.stringify({ mode: 'modash', profile_url: c.profile_url }),
        });
        success++;
      } catch { /* continue */ }
      setProgress(prev => ({ ...prev, done: i + 1, success }));
    }
    setProgress(prev => ({ ...prev, done: toRun.length, success, current: '' }));
    setStep('done');
  }

  const stepLabels = ['Import', 'Review', 'Duplicates', 'Run'];
  const stepKeys   = ['input', 'review', 'duplicates', 'progress'];
  const currentIdx = stepKeys.indexOf(step === 'done' ? 'progress' : step);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ backgroundColor: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(4px)' }}
    >
      <div
        className="w-full max-w-2xl mx-4"
        style={{ backgroundColor: '#111', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '4px' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4" style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          <div className="flex items-center gap-2">
            <Upload className="w-4 h-4 text-gold" />
            <h2 className="text-base font-semibold text-white font-display">Mass Import & Match</h2>
          </div>
          <button onClick={onClose} className="text-white/40 hover:text-white/70 transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Step indicator */}
        <div className="flex items-center px-6 pt-4 gap-1.5">
          {stepLabels.map((label, i) => {
            const isDone   = i < currentIdx || (step === 'done' && i === stepKeys.length - 1);
            const isActive = i === currentIdx;
            return (
              <div key={label} className="flex items-center gap-1.5">
                <div
                  className="flex items-center justify-center w-5 h-5 text-[10px] font-bold shrink-0"
                  style={{
                    borderRadius: '50%',
                    backgroundColor: isDone || isActive ? '#FFD400' : 'rgba(255,255,255,0.08)',
                    color: isDone || isActive ? '#000' : 'rgba(255,255,255,0.3)',
                  }}
                >
                  {isDone && i < currentIdx ? '✓' : i + 1}
                </div>
                <span className="text-xs" style={{ color: isDone || isActive ? 'rgba(255,255,255,0.7)' : 'rgba(255,255,255,0.3)' }}>
                  {label}
                </span>
                {i < stepLabels.length - 1 && <ChevronRight className="w-3 h-3 text-white/20" />}
              </div>
            );
          })}
        </div>

        {/* Step content */}
        <div className="px-6 pb-6 pt-4" style={{ minHeight: '260px' }}>
          {/* Step 1: Input */}
          {step === 'input' && (
            <div className="space-y-4">
              <p className="text-sm text-white/55">
                Paste a Google Sheet URL (published as CSV) or paste rows directly. Each row needs a social media link.
              </p>
              <textarea
                value={sheetInput}
                onChange={e => setSheetInput(e.target.value)}
                placeholder={`Google Sheet URL:\nhttps://docs.google.com/spreadsheets/d/...\n\nOr paste CSV rows:\nEmma Chamberlain, https://www.instagram.com/emmachamberlain\nhttps://www.tiktok.com/@charlidamelio`}
                rows={8}
                className="w-full bg-transparent text-sm text-white/80 placeholder-white/20 resize-none focus:outline-none font-mono"
                style={{ border: '1px solid rgba(255,255,255,0.1)', borderRadius: '2px', padding: '12px' }}
              />
              {fetchErr && <p className="text-xs text-red-400">{fetchErr}</p>}
              <div className="flex justify-end">
                <button
                  onClick={handleParse}
                  disabled={fetching || !sheetInput.trim()}
                  className="btn-primary text-sm px-4 py-2 flex items-center gap-2 disabled:opacity-40"
                >
                  {fetching && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                  Parse Influencers
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Review */}
          {step === 'review' && (
            <div className="space-y-3">
              <p className="text-sm text-white/55">
                Detected <span className="text-white/80 font-medium">{parsed.length}</span> creators.
                Remove any rows you don't want to match.
                {undetected.length > 0 && (
                  <span className="text-amber-400 ml-1">({undetected.length} rows not recognized)</span>
                )}
              </p>
              <div className="overflow-y-auto space-y-1" style={{ maxHeight: '280px' }}>
                {parsed.map((c, i) => (
                  <div
                    key={i}
                    className={`flex items-center gap-3 py-2 px-3 transition-opacity ${removed.has(i) ? 'opacity-30' : ''}`}
                    style={{ backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '2px' }}
                  >
                    <PlatformBadge platform={c.platform} />
                    <span className="text-sm text-white/80 font-mono flex-1 truncate">{c.handle}</span>
                    {c.name && <span className="text-xs text-white/35 truncate max-w-[120px]">{c.name}</span>}
                    <button
                      onClick={() => setRemoved(prev => {
                        const next = new Set(prev);
                        next.has(i) ? next.delete(i) : next.add(i);
                        return next;
                      })}
                      className="text-xs px-2 py-0.5 shrink-0 transition-colors"
                      style={{
                        color: removed.has(i) ? '#4ade80' : '#f87171',
                        border: `1px solid ${removed.has(i) ? '#4ade8040' : '#f8717140'}`,
                        borderRadius: '2px',
                      }}
                    >
                      {removed.has(i) ? 'Restore' : 'Remove'}
                    </button>
                  </div>
                ))}
                {parsed.length === 0 && (
                  <p className="text-sm text-white/35 text-center py-8">
                    No creators detected. Check your input format.
                  </p>
                )}
              </div>
              <div className="flex justify-between pt-1">
                <button onClick={() => setStep('input')} className="btn-ghost text-sm px-4 py-2">Back</button>
                <button
                  onClick={handleReviewNext}
                  disabled={parsed.filter((_, i) => !removed.has(i)).length === 0}
                  className="btn-primary text-sm px-4 py-2 disabled:opacity-40"
                >
                  Continue ({parsed.filter((_, i) => !removed.has(i)).length})
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Duplicates */}
          {step === 'duplicates' && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-amber-400 shrink-0" />
                <p className="text-sm text-amber-400">
                  {duplicates.length} influencer{duplicates.length !== 1 ? 's have' : ' has'} already been matched.
                </p>
              </div>
              <p className="text-xs text-white/40">Toggle "Re-run" to match them again, or leave as "Skipped".</p>
              <div className="overflow-y-auto space-y-1" style={{ maxHeight: '200px' }}>
                {duplicates.map((d, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-3 py-2 px-3"
                    style={{ backgroundColor: 'rgba(251,191,36,0.04)', border: '1px solid rgba(251,191,36,0.12)', borderRadius: '2px' }}
                  >
                    <span className="text-sm font-mono text-white/80 flex-1 truncate">{d.handle}</span>
                    <span className="text-xs text-white/35 shrink-0">Last: {fmtDate(d.lastMatchDate)}</span>
                    <button
                      onClick={() => setDuplicates(prev => {
                        const next = [...prev];
                        next[i] = { ...next[i], skip: !next[i].skip };
                        return next;
                      })}
                      className="text-xs px-2 py-0.5 shrink-0 transition-colors"
                      style={{
                        color: d.skip ? 'rgba(255,255,255,0.35)' : '#FFD400',
                        border: `1px solid ${d.skip ? 'rgba(255,255,255,0.1)' : 'rgba(255,212,0,0.4)'}`,
                        borderRadius: '2px',
                      }}
                    >
                      {d.skip ? 'Skipped' : 'Re-run'}
                    </button>
                  </div>
                ))}
              </div>
              <div className="flex justify-between pt-1">
                <button onClick={() => setStep('review')} className="btn-ghost text-sm px-4 py-2">Back</button>
                <button
                  onClick={() => startBatch(parsed.filter((_, i) => !removed.has(i)), duplicates)}
                  className="btn-primary text-sm px-4 py-2"
                >
                  Start Matching
                </button>
              </div>
            </div>
          )}

          {/* Step 4: Progress */}
          {step === 'progress' && (
            <div className="flex flex-col items-center justify-center space-y-5 py-8">
              <Loader2 className="w-6 h-6 animate-spin text-gold" />
              <p className="text-sm text-white/70">
                {progress.current ? `Matching ${progress.current}…` : 'Preparing…'}
              </p>
              <div className="w-full max-w-sm h-2 rounded-full" style={{ backgroundColor: 'rgba(255,255,255,0.08)' }}>
                <div
                  className="h-2 rounded-full transition-all duration-500"
                  style={{
                    width: `${(progress.done / Math.max(progress.total, 1)) * 100}%`,
                    backgroundColor: '#FFD400',
                  }}
                />
              </div>
              <p className="text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>
                {progress.done} / {progress.total} complete
              </p>
            </div>
          )}

          {/* Done */}
          {step === 'done' && (
            <div className="flex flex-col items-center justify-center space-y-4 py-8 text-center">
              <CheckCircle2 className="w-10 h-10 text-green-400" />
              <p className="text-base font-semibold text-white">
                {progress.success} of {progress.total} matches completed
              </p>
              <p className="text-sm text-white/40">Results are now visible in the table below.</p>
              <button onClick={() => { onImportComplete(); onClose(); }} className="btn-primary px-6 py-2 text-sm">
                View Results
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── ExpandedRow ───────────────────────────────────────────────────────────

function ExpandedRow({
  run,
  results,
  loading,
  onPdfDownload,
}: {
  run: RunSummary;
  results: RunResultItem[] | null;
  loading: boolean;
  onPdfDownload: () => void;
}) {
  const [imgErrors, setImgErrors] = useState<Set<number>>(new Set());
  const ca = run.creator_analyses;

  return (
    <div style={{ borderTop: '1px solid rgba(255,255,255,0.06)', backgroundColor: 'rgba(255,255,255,0.012)' }}>
      {/* Tags strip */}
      <div className="px-5 pt-4 pb-2 flex flex-wrap gap-5">
        {ca.interests_ranked && ca.interests_ranked.length > 0 && (
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-white/25 mb-1.5">Top Interests</p>
            <div className="flex flex-wrap gap-1">
              {ca.interests_ranked.slice(0, 6).map((ir, idx) => (
                <span
                  key={idx}
                  className="px-2 py-0.5 text-[11px] text-white/55"
                  style={{ backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '2px' }}
                >
                  {ir.label}
                </span>
              ))}
            </div>
          </div>
        )}
        {ca.brand_affinity_ranked && ca.brand_affinity_ranked.length > 0 && (
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-white/25 mb-1.5">Brand Affinity</p>
            <div className="flex flex-wrap gap-1">
              {ca.brand_affinity_ranked.slice(0, 6).map((br, idx) => (
                <span
                  key={idx}
                  className="px-2 py-0.5 text-[11px] text-white/55"
                  style={{ backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '2px' }}
                >
                  {br.label}
                </span>
              ))}
            </div>
          </div>
        )}
        {ca.audience_gender_affinity && (
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-white/25 mb-1.5">Audience</p>
            <span className="px-2 py-0.5 text-[11px] capitalize text-white/55" style={{ backgroundColor: 'rgba(167,139,250,0.08)', border: '1px solid rgba(167,139,250,0.15)', borderRadius: '2px' }}>
              {ca.audience_gender_affinity}-skew
            </span>
          </div>
        )}
        {run.assumptions?.summary_text && (
          <div className="flex-1" style={{ minWidth: '200px' }}>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-white/25 mb-1.5">AI Summary</p>
            <p className="text-[12px] text-white/45 leading-relaxed" style={{ maxWidth: '420px' }}>
              {String(run.assumptions.summary_text)}
            </p>
          </div>
        )}
        {/* Audience quality */}
        {(() => {
          const caAny = ca as Record<string, unknown>;
          const cred = caAny.credibility_score as number | null | undefined;
          const ppp = caAny.paid_post_performance as number | null | undefined;
          if (cred == null && ppp == null) return null;
          const credPct = cred != null ? Math.round(cred * 100) : null;
          const credColor = cred != null
            ? cred >= 0.80 ? '#4ade80' : cred >= 0.65 ? '#facc15' : '#f87171'
            : null;
          return (
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-white/25 mb-1.5">Audience Quality</p>
              {credPct != null && (
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-bold" style={{ color: credColor! }}>{credPct}% Real</span>
                  <div className="w-20 h-1.5 rounded-sm overflow-hidden" style={{ backgroundColor: 'rgba(255,255,255,0.06)' }}>
                    <div className="h-full" style={{ width: `${credPct}%`, backgroundColor: credColor! }} />
                  </div>
                </div>
              )}
              {ppp != null && (
                <span className="text-[11px] text-white/40">PPP: {Math.round(ppp * 100)}%</span>
              )}
            </div>
          );
        })()}
        {/* Bio */}
        {(() => {
          const notes = ((ca as Record<string, unknown>).notes ?? {}) as Record<string, unknown>;
          const bio = notes.bio as string | null | undefined;
          if (!bio?.trim()) return null;
          return (
            <div className="flex-1" style={{ minWidth: '200px' }}>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-white/25 mb-1.5">Bio</p>
              <p className="text-[11px] text-white/45 leading-relaxed line-clamp-3">{bio}</p>
            </div>
          );
        })()}
        {/* Languages */}
        {(() => {
          const caAny = ca as Record<string, unknown>;
          const langs = caAny.audience_languages as Array<{ code: string; name?: string; weight: number }> | null | undefined;
          if (!langs?.length) return null;
          return (
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-white/25 mb-1.5">Languages</p>
              <div className="flex flex-wrap gap-1">
                {langs.slice(0, 4).map((l, i) => (
                  <span key={i} className="px-2 py-0.5 text-[11px] text-white/55"
                    style={{ backgroundColor: 'rgba(96,165,250,0.08)', border: '1px solid rgba(96,165,250,0.15)', borderRadius: '2px' }}>
                    {l.name ?? l.code} {Math.round(l.weight * 100)}%
                  </span>
                ))}
              </div>
            </div>
          );
        })()}
      </div>

      {/* Product results */}
      <div className="px-5 pb-5">
        <div className="flex items-center justify-between mb-2.5">
          <p className="text-[10px] font-semibold text-white/30 uppercase tracking-wider">Top Matched Products</p>
          <button
            onClick={onPdfDownload}
            disabled={loading || !results}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors disabled:opacity-40"
            style={{ color: '#FFD400', border: '1px solid rgba(255,212,0,0.3)', borderRadius: '2px' }}
          >
            <FileDown className="w-3.5 h-3.5" />
            Download PDF
          </button>
        </div>
        {loading ? (
          <div className="flex items-center gap-2 py-5 text-white/35 text-sm">
            <Loader2 className="w-4 h-4 animate-spin" />Loading products…
          </div>
        ) : results && results.length > 0 ? (
          <div className="space-y-1.5">
            {results.map(r => (
              <div
                key={r.rank}
                className="flex items-center gap-3 p-3"
                style={{ backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '2px' }}
              >
                <span className="text-xs font-bold text-white/20 w-5 shrink-0">#{r.rank}</span>
                {r.products?.primary_image_url && !imgErrors.has(r.products.id) ? (
                  <img
                    src={r.products.primary_image_url}
                    referrerPolicy="no-referrer"
                    className="w-11 h-11 object-cover shrink-0"
                    style={{ border: '1px solid rgba(255,255,255,0.08)', borderRadius: '2px' }}
                    alt=""
                    onError={() => setImgErrors(prev => new Set([...prev, r.products!.id]))}
                  />
                ) : (
                  <div
                    className="w-11 h-11 flex items-center justify-center shrink-0"
                    style={{ backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '2px' }}
                  >
                    <Package className="w-4 h-4 text-white/25" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-white/85 truncate">{r.products?.title ?? '—'}</p>
                  <p className="text-xs text-white/35">
                    {r.products?.vertical} · ${r.products?.price_min}–${r.products?.price_max}
                  </p>
                </div>
                <ScoreChip score={r.score} />
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-white/35 py-4">No products loaded.</p>
        )}
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────

export default function AdminExploreInfluencers() {
  const { isDemoMode } = useDemo();

  const [runs, setRuns]             = useState<RunSummary[]>([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState<string | null>(null);
  const [expanded, setExpanded]     = useState<number | null>(null);
  const [runDetails, setRunDetails] = useState<Record<number, RunResultItem[]>>({});
  const [detailLoading, setDetailLoading] = useState<number | null>(null);
  const [showImport, setShowImport] = useState(false);

  // Filters
  const [search, setSearch]               = useState('');
  const [platformFilter, setPlatform]     = useState<string | null>(null);
  const [tierFilter, setTier]             = useState<number | null>(null);
  const [interestFilter, setInterest]     = useState<string | null>(null);
  const [brandFilter, setBrand]           = useState<string | null>(null);
  const [genderFilter, setGender]         = useState<string | null>(null);
  const [dedupe, setDedupe]               = useState(true);

  // Dropdown state
  const [interestSearch, setInterestSearch] = useState('');
  const [brandSearch, setBrandSearch]       = useState('');
  const [interestOpen, setInterestOpen]     = useState(false);
  const [brandOpen, setBrandOpen]           = useState(false);
  const interestRef = useRef<HTMLDivElement>(null);
  const brandRef    = useRef<HTMLDivElement>(null);

  async function loadRuns() {
    setLoading(true);
    setError(null);
    if (isDemoMode) {
      setRuns(DEMO_EXPLORE_RUNS as unknown as RunSummary[]);
      setLoading(false);
      return;
    }
    try {
      const d = await apiFetch<{ runs: RunSummary[] }>('/runs?limit=500');
      setRuns(d.runs);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load runs');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadRuns(); }, [isDemoMode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close dropdowns on outside click
  useEffect(() => {
    function onDown(e: MouseEvent) {
      if (interestRef.current && !interestRef.current.contains(e.target as Node)) setInterestOpen(false);
      if (brandRef.current    && !brandRef.current.contains(e.target as Node))    setBrandOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  async function toggleRun(id: number) {
    if (expanded === id) { setExpanded(null); return; }
    if (isDemoMode) { setExpanded(id); return; }
    if (runDetails[id]) { setExpanded(id); return; }
    setDetailLoading(id);
    try {
      const d = await apiFetch<{ run: unknown; results: RunResultItem[] }>(`/runs/${id}`);
      setRunDetails(prev => ({ ...prev, [id]: d.results }));
      setExpanded(id);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load run');
    } finally {
      setDetailLoading(null);
    }
  }

  async function handlePdfDownload(run: RunSummary) {
    let results = runDetails[run.id];
    if (!results && isDemoMode) {
      results = DEMO_RUN_RESULTS as unknown as RunResultItem[];
    }
    if (!results) {
      setDetailLoading(run.id);
      try {
        const d = await apiFetch<{ run: unknown; results: RunResultItem[] }>(`/runs/${run.id}`);
        results = d.results;
        setRunDetails(prev => ({ ...prev, [run.id]: results }));
      } catch { return; } finally { setDetailLoading(null); }
    }
    if (!results) return;
    const analyzeResult = buildAnalyzeResult(run, results);
    await exportMatchReport(analyzeResult as Parameters<typeof exportMatchReport>[0], null);
  }

  // ── Derived filter options ─────────────────────────────────────────────
  const { allInterests, allBrands } = useMemo(() => {
    const iMap = new Map<string, number>();
    const bMap = new Map<string, number>();
    for (const r of runs) {
      for (const ir of (r.creator_analyses?.interests_ranked ?? [])) {
        iMap.set(ir.label, (iMap.get(ir.label) ?? 0) + 1);
      }
      for (const br of (r.creator_analyses?.brand_affinity_ranked ?? [])) {
        bMap.set(br.label, (bMap.get(br.label) ?? 0) + 1);
      }
    }
    const sort = (m: Map<string, number>) => [...m.entries()].sort((a, b) => b[1] - a[1]).map(e => e[0]);
    return { allInterests: sort(iMap), allBrands: sort(bMap) };
  }, [runs]);

  // ── Filter + dedupe ────────────────────────────────────────────────────
  const displayed = useMemo(() => {
    let result = runs;
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      result = result.filter(r =>
        (r.creator_analyses?.creators?.handle ?? '').toLowerCase().includes(q) ||
        (r.creator_analyses?.primary_category ?? '').toLowerCase().includes(q),
      );
    }
    if (platformFilter) {
      result = result.filter(r => {
        const details = r.assumptions?.platform_details;
        if (details?.length) return details.some(d => d.platform === platformFilter);
        return r.creator_analyses?.creators?.platform === platformFilter;
      });
    }
    if (tierFilter !== null) result = result.filter(r => r.tier === tierFilter);
    if (interestFilter) {
      result = result.filter(r =>
        (r.creator_analyses?.interests_ranked ?? []).some(ir => ir.label === interestFilter),
      );
    }
    if (brandFilter) {
      result = result.filter(r =>
        (r.creator_analyses?.brand_affinity_ranked ?? []).some(br => br.label === brandFilter),
      );
    }
    if (genderFilter) {
      result = result.filter(r => {
        const g = (r.creator_analyses?.audience_gender_affinity ?? '').toLowerCase();
        if (genderFilter === 'female') return g === 'female' || g.includes('female');
        if (genderFilter === 'male')   return g === 'male'   || g.includes('male');
        return true;
      });
    }
    if (dedupe) {
      const seen = new Set<string>();
      result = result.filter(r => {
        const key = (r.creator_analyses?.creators?.handle ?? `run-${r.id}`).toLowerCase();
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    }
    return result;
  }, [runs, search, platformFilter, tierFilter, interestFilter, brandFilter, genderFilter, dedupe]);

  const filtInterests = allInterests.filter(i => i.toLowerCase().includes(interestSearch.toLowerCase()));
  const filtBrands    = allBrands.filter(b => b.toLowerCase().includes(brandSearch.toLowerCase()));
  const anyFilter     = !!(search || platformFilter || tierFilter !== null || interestFilter || brandFilter || genderFilter);

  function clearAllFilters() {
    setSearch(''); setPlatform(null); setTier(null); setInterest(null); setBrand(null); setGender(null);
  }

  // ── Render ─────────────────────────────────────────────────────────────
  return (
    <div className="p-6 lg:p-8 max-w-7xl">
      <PageHeader
        title="Explore Influencers"
        subtitle={`${displayed.length} creator${displayed.length !== 1 ? 's' : ''}${runs.length !== displayed.length ? ` (filtered from ${runs.length})` : ''}`}
        actions={
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowImport(true)}
              className="btn-secondary text-sm flex items-center gap-1.5 px-3 py-2"
            >
              <Upload className="w-3.5 h-3.5" /> Mass Import
            </button>
            <a href="/admin/creator-match" className="btn-primary text-sm flex items-center gap-1.5 px-3 py-2">
              <Sparkles className="w-3.5 h-3.5" /> New Match
            </a>
          </div>
        }
      />

      {/* ── Filter bar ──────────────────────────────────────────────────── */}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/30" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search handle or category"
            className="bg-transparent text-sm text-white placeholder-white/25 pl-8 pr-3 py-1.5 focus:outline-none"
            style={{ border: '1px solid rgba(255,255,255,0.1)', borderRadius: '2px', width: '220px' }}
          />
        </div>

        {/* Platform chips */}
        {(['instagram', 'tiktok', 'youtube'] as const).map(p => (
          <button
            key={p}
            onClick={() => setPlatform(prev => prev === p ? null : p)}
            className="px-2.5 py-1 text-[11px] font-bold uppercase transition-colors"
            style={{
              borderRadius: '2px',
              border: platformFilter === p ? `1px solid ${PLATFORM_COLORS[p]}` : '1px solid rgba(255,255,255,0.1)',
              backgroundColor: platformFilter === p ? `${PLATFORM_COLORS[p]}18` : 'transparent',
              color: platformFilter === p ? PLATFORM_COLORS[p] : 'rgba(255,255,255,0.4)',
            }}
          >
            {PLATFORM_LABEL[p]}
          </button>
        ))}

        {/* Tier chips */}
        {[1, 2, 3].map(t => (
          <button
            key={t}
            onClick={() => setTier(prev => prev === t ? null : t)}
            className="px-2.5 py-1 text-[11px] font-semibold transition-colors"
            style={{
              borderRadius: '2px',
              border: tierFilter === t ? '1px solid rgba(255,212,0,0.5)' : '1px solid rgba(255,255,255,0.1)',
              backgroundColor: tierFilter === t ? 'rgba(255,212,0,0.1)' : 'transparent',
              color: tierFilter === t ? '#FFD400' : 'rgba(255,255,255,0.4)',
            }}
          >
            T{t}
          </button>
        ))}

        {/* Interest dropdown */}
        <div className="relative" ref={interestRef}>
          <button
            onClick={() => setInterestOpen(p => !p)}
            className="flex items-center gap-1.5 px-2.5 py-1 text-[11px] transition-colors"
            style={{
              borderRadius: '2px',
              border: interestFilter ? '1px solid rgba(255,212,0,0.5)' : '1px solid rgba(255,255,255,0.1)',
              backgroundColor: interestFilter ? 'rgba(255,212,0,0.08)' : 'transparent',
              color: interestFilter ? '#FFD400' : 'rgba(255,255,255,0.4)',
            }}
          >
            <Filter className="w-3 h-3" />
            {interestFilter ?? 'Interest'}
            {interestFilter && (
              <X
                className="w-3 h-3 cursor-pointer"
                onClick={e => { e.stopPropagation(); setInterest(null); }}
              />
            )}
          </button>
          {interestOpen && (
            <div
              className="absolute top-full left-0 z-20 w-56 mt-1"
              style={{ backgroundColor: '#111', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '2px' }}
            >
              <div className="p-2">
                <input
                  value={interestSearch}
                  onChange={e => setInterestSearch(e.target.value)}
                  placeholder="Search interests…"
                  className="w-full bg-transparent text-xs text-white placeholder-white/30 px-2 py-1.5 focus:outline-none"
                  style={{ border: '1px solid rgba(255,255,255,0.1)', borderRadius: '2px' }}
                />
              </div>
              <div className="overflow-y-auto" style={{ maxHeight: '200px' }}>
                {filtInterests.map(i => (
                  <button
                    key={i}
                    onClick={() => { setInterest(i); setInterestOpen(false); setInterestSearch(''); }}
                    className="w-full text-left px-3 py-1.5 text-xs hover:bg-white/[0.04] transition-colors"
                    style={{ color: interestFilter === i ? '#FFD400' : 'rgba(255,255,255,0.65)' }}
                  >
                    {i}
                  </button>
                ))}
                {filtInterests.length === 0 && (
                  <p className="px-3 py-2 text-xs text-white/30">No matches</p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Brand dropdown */}
        <div className="relative" ref={brandRef}>
          <button
            onClick={() => setBrandOpen(p => !p)}
            className="flex items-center gap-1.5 px-2.5 py-1 text-[11px] transition-colors"
            style={{
              borderRadius: '2px',
              border: brandFilter ? '1px solid rgba(255,212,0,0.5)' : '1px solid rgba(255,255,255,0.1)',
              backgroundColor: brandFilter ? 'rgba(255,212,0,0.08)' : 'transparent',
              color: brandFilter ? '#FFD400' : 'rgba(255,255,255,0.4)',
            }}
          >
            <Filter className="w-3 h-3" />
            {brandFilter ?? 'Brand'}
            {brandFilter && (
              <X
                className="w-3 h-3 cursor-pointer"
                onClick={e => { e.stopPropagation(); setBrand(null); }}
              />
            )}
          </button>
          {brandOpen && (
            <div
              className="absolute top-full left-0 z-20 w-56 mt-1"
              style={{ backgroundColor: '#111', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '2px' }}
            >
              <div className="p-2">
                <input
                  value={brandSearch}
                  onChange={e => setBrandSearch(e.target.value)}
                  placeholder="Search brands…"
                  className="w-full bg-transparent text-xs text-white placeholder-white/30 px-2 py-1.5 focus:outline-none"
                  style={{ border: '1px solid rgba(255,255,255,0.1)', borderRadius: '2px' }}
                />
              </div>
              <div className="overflow-y-auto" style={{ maxHeight: '200px' }}>
                {filtBrands.map(b => (
                  <button
                    key={b}
                    onClick={() => { setBrand(b); setBrandOpen(false); setBrandSearch(''); }}
                    className="w-full text-left px-3 py-1.5 text-xs hover:bg-white/[0.04] transition-colors"
                    style={{ color: brandFilter === b ? '#FFD400' : 'rgba(255,255,255,0.65)' }}
                  >
                    {b}
                  </button>
                ))}
                {filtBrands.length === 0 && (
                  <p className="px-3 py-2 text-xs text-white/30">No matches</p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Gender filter */}
        {(['female', 'male'] as const).map(g => (
          <button
            key={g}
            onClick={() => setGender(prev => prev === g ? null : g)}
            className="px-2.5 py-1 text-[11px] capitalize transition-colors"
            style={{
              borderRadius: '2px',
              border: genderFilter === g ? '1px solid rgba(167,139,250,0.5)' : '1px solid rgba(255,255,255,0.1)',
              backgroundColor: genderFilter === g ? 'rgba(167,139,250,0.1)' : 'transparent',
              color: genderFilter === g ? '#a78bfa' : 'rgba(255,255,255,0.4)',
            }}
          >
            {g === 'female' ? '♀ F-skew' : '♂ M-skew'}
          </button>
        ))}

        {/* Dedupe toggle */}
        <button
          onClick={() => setDedupe(p => !p)}
          className="px-2.5 py-1 text-[11px] transition-colors"
          style={{
            borderRadius: '2px',
            border: dedupe ? '1px solid rgba(74,222,128,0.4)' : '1px solid rgba(255,255,255,0.1)',
            backgroundColor: dedupe ? 'rgba(74,222,128,0.06)' : 'transparent',
            color: dedupe ? '#4ade80' : 'rgba(255,255,255,0.4)',
          }}
        >
          Latest only
        </button>

        {/* Clear */}
        {anyFilter && (
          <button
            onClick={clearAllFilters}
            className="flex items-center gap-1 text-[11px] text-white/35 hover:text-white/60 transition-colors px-1"
          >
            <X className="w-3 h-3" /> Clear
          </button>
        )}
      </div>

      {/* ── Table ──────────────────────────────────────────────────────── */}
      {loading ? (
        <DataTableSkeleton rows={8} />
      ) : error ? (
        <div className="text-red-400 text-sm py-8 text-center">{error}</div>
      ) : displayed.length === 0 ? (
        <EmptyState
          icon={<Users className="w-8 h-8" />}
          title="No influencers found"
          message={
            runs.length === 0
              ? "Run Creator Match to start building your influencer database."
              : "No results match your current filters."
          }
        />
      ) : (
        <div style={{ border: '1px solid rgba(255,255,255,0.08)', borderRadius: '4px', overflow: 'hidden' }}>
          <table className="w-full border-collapse table-fixed">
            <colgroup>
              <col style={{ width: '52px' }} />
              <col style={{ width: '200px' }} />
              <col style={{ width: '110px' }} />
              <col style={{ width: '100px' }} />
              <col style={{ width: '140px' }} />
              <col style={{ width: '78px' }} />
              <col style={{ width: '110px' }} />
              <col style={{ width: '40px' }} />
            </colgroup>
            <thead>
              <tr style={{ backgroundColor: 'rgba(255,255,255,0.02)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                <th className="px-3 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wider text-white/30" />
                <th className="px-3 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wider text-white/30">Creator</th>
                <th className="px-3 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wider text-white/30">Category</th>
                <th className="px-3 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wider text-white/30">Followers</th>
                <th className="px-3 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wider text-white/30">Est. GMV</th>
                <th className="px-3 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wider text-white/30">Tier</th>
                <th className="px-3 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wider text-white/30">Matched</th>
                <th className="px-3 py-2.5" />
              </tr>
            </thead>
            <tbody>
              {displayed.map(run => {
                const ca         = run.creator_analyses;
                const cr         = ca?.creators;
                const isExpanded = expanded === run.id;
                const isLoading  = detailLoading === run.id;
                const platforms  = run.assumptions?.platform_details?.length
                  ? run.assumptions.platform_details
                  : [{ platform: cr?.platform ?? '', handle: cr?.handle ?? '' }];

                return (
                  <AnimatePresence key={run.id}>
                    <tr
                      onClick={() => toggleRun(run.id)}
                      className="cursor-pointer transition-colors"
                      style={{ borderBottom: isExpanded ? 'none' : '1px solid rgba(255,255,255,0.05)' }}
                      onMouseEnter={e => { if (!isExpanded) (e.currentTarget as HTMLTableRowElement).style.backgroundColor = 'rgba(255,255,255,0.015)'; }}
                      onMouseLeave={e => { if (!isExpanded) (e.currentTarget as HTMLTableRowElement).style.backgroundColor = ''; }}
                    >
                      <td className="px-3 py-3">
                        <CreatorAvatar url={ca?.profile_image_url ?? null} handle={cr?.handle ?? '?'} size={34} />
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-1 flex-wrap mb-0.5">
                          {platforms.map(p => <PlatformBadge key={p.platform} platform={p.platform} />)}
                        </div>
                        <p className="text-sm text-white/85 font-medium truncate">{cr?.handle ?? '—'}</p>
                      </td>
                      <td className="px-3 py-3">
                        <span className="text-xs text-white/50 capitalize">{ca?.primary_category ?? '—'}</span>
                      </td>
                      <td className="px-3 py-3">
                        <span className="text-sm text-white/70">{fmtNum(ca?.followers ?? null)}</span>
                        {ca?.engagement_rate != null && (
                          <p className="text-[11px] text-white/30">{(ca.engagement_rate * 100).toFixed(1)}% eng</p>
                        )}
                      </td>
                      <td className="px-3 py-3">
                        <p className="text-sm font-semibold text-white/85">{fmtMoney(run.gmv_expected)}</p>
                        <p className="text-[11px] text-white/30">{fmtMoney(run.gmv_low)}–{fmtMoney(run.gmv_high)}</p>
                      </td>
                      <td className="px-3 py-3">
                        <TierBadge tier={run.tier} />
                      </td>
                      <td className="px-3 py-3">
                        <span className="text-xs text-white/40">{fmtDate(run.created_at)}</span>
                      </td>
                      <td className="px-3 py-3 text-right">
                        {isLoading
                          ? <Loader2 className="w-4 h-4 animate-spin text-white/30 inline" />
                          : isExpanded
                            ? <ChevronUp className="w-4 h-4 text-white/30 inline" />
                            : <ChevronDown className="w-4 h-4 text-white/30 inline" />}
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr
                        key={`${run.id}-detail`}
                        style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}
                      >
                        <td colSpan={8} className="p-0">
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.18 }}
                          >
                            <ExpandedRow
                              run={run}
                              results={runDetails[run.id] ?? (isDemoMode ? DEMO_RUN_RESULTS as unknown as RunResultItem[] : null)}
                              loading={detailLoading === run.id}
                              onPdfDownload={() => handlePdfDownload(run)}
                            />
                          </motion.div>
                        </td>
                      </tr>
                    )}
                  </AnimatePresence>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showImport && (
        <MassImportModal
          onClose={() => setShowImport(false)}
          existingRuns={runs}
          onImportComplete={loadRuns}
        />
      )}
    </div>
  );
}
